import { useEffect, useState } from 'react'
import { RefundTOS } from './RefundCalculatedSteps/RefundTOS'
import { AdvancePaymentDetails } from './RefundCalculatedSteps/AdvancePaymentDetails'
import { AdvancePaymentBankVerification } from './RefundCalculatedSteps/AdvancePaymentBankVerification'
import { RefundOptions } from './RefundCalculatedSteps/RefundOptions'
import { AdvanceFundingApplication, company, customerData, ValidiFiBankVerification } from '../../utilities/CustomerData'
import { RefundGenerateInvoice } from './RefundCalculatedSteps/RefundGenerateInvoice'
import { refreshAccessToken } from '../../utilities/extensions'
import { RefundShippo } from './RefundCalculatedSteps/RefundShippo'
import { useNavigate } from 'react-router-dom'
import { apiURL } from '../../utilities/URLs'
import { RefundInfo } from './RefundCalculatedSteps/RefundInfo'
import { IDMEVerification } from '../IDMEVerification'
import posthog from 'posthog-js'
import { RefundShippingOptions } from './RefundCalculatedSteps/RefundShippingOptions'
import RefundLockBox from './RefundCalculatedSteps/RefundLockBox'
import RefundSpouseMultipleIncome from './RefundCalculatedSteps/RefundSpouseMultipleIncome'

export default function RefundCalculated({ setReload }) {
  const navigate = useNavigate()

  const [application, setApplication] = useState(customerData.customerAdvanceFunding)
  const [step, setStep] = useState(0)
  const [refundType, setRefundType] = useState('')
  const [refundRate, setRefundRate] = useState(0)
  const [lienAmount, setLienAmount] = useState(customerData.customerAdvanceFunding.lienAmount)
  const [interestAmount, setInterestAmount] = useState(customerData.customerAdvanceFunding.interestAmount)
  const [loanInterestAmount, setLoanInterestAmount] = useState(customerData.customerAdvanceFunding.loanInterestAmount)
  const [code, setCode] = useState(new URLSearchParams(window.location.search).get('code'))
  const [showKYC, setShowKYC] = useState(customerData.customerVerificationData.verificationId ? false : true)
  const [showSpouseIncome, setShowSpouseIncome] = useState(customerData.customerMultipleSourceIncome.incomeId && !customerData.customerMultipleSourceIncome.didSpouseCalculation)
  const [kycError, setKYCError] = useState('')
  const [grabbingKYCData, setGrabbingKYCData] = useState(false)
  const [shippingOption, setShippingOption] = useState('')
  const [showLockBox, setShowLockBox] = useState(!customerData.customerLockBoxInfo.lockBoxId)

  useEffect(() => {
    const handleExchangeIDMECode = async () => {
      setGrabbingKYCData(true)
      const form = new FormData()
      form.append('codeInfo', JSON.stringify({
        customerId: customerData.customerLeadData.leadContact.contactId,
        code: code
      }))

      const exchangeCodeResponse = await fetch(`${apiURL}/api/exchangeCodeIDME`, {
        method: 'POST',
        body: form
      })
      const exchangeCodeData = await exchangeCodeResponse.json()
      setGrabbingKYCData(false)
      if(exchangeCodeData.response.error) {
        if(exchangeCodeData.response.error === 'Expired Documents') {
          navigate('/syncNewDocuments', { state: { upload: false }})
        } else {
          if(exchangeCodeData.response.error === 'SSN is not found/not matching.')
            setKYCError('The last 4 digits of your SSN do not match our records. Please make sure you are using the correct ID.me account linked to your SSN.')
          else
            setKYCError('We ran into an issue retrieving your ID.me information. Please try again by clicking the button above.')

          setShowKYC(true)
        }
      } else {
        if (!['d195503d-081a-4d94-a413-6e02bb83d40c', '034d6ab6-f2dc-47b9-a420-63df42b87381'].includes(customerData.customerLeadData.leadStage.leadStageId))
          navigate('/dashboard')
      }
    }

    if(code) {
      handleExchangeIDMECode()
      navigate('/refundCalculated')
      setShowKYC(false)
      setStep(1)
    }
  }, [code])

  const handleAdvanceChosenStatus = async () => {
    const form = new FormData()
    form.append('upsertInfo', JSON.stringify({
      customer_id: 1,
      customerId: customerData.customerLeadData.leadContact.contactId,
      first_name: customerData.customerLeadData.leadContact.contactFirstName,
      last_name: customerData.customerLeadData.leadContact.contactLastName,
      email: customerData.customerLeadData.leadContact.contactEmail,
      last4SSN: customerData.customerLeadData.leadContact.contactLast4SSN,
      birthYear: customerData.customerLeadData.leadContact.contactBirthYear,
      current_status: 'advance_chosen'
    }))

    const upsertCustomerResponse = await fetch(`${apiURL}/api/upsertCustomerStatus`, {
      method: 'PUT',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const upsertCustomerData = await upsertCustomerResponse.json()
    if(upsertCustomerData.response.error) {
      if(upsertCustomerData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleAdvanceChosenStatus() : navigate('/', { state: { expired: true } })
      } else
        console.error(upsertCustomerData.response.error)
    }
  }

  const handleSelectRefundType = async (refundType, refundRate) => {
    setRefundType(refundType)
    setRefundRate(refundRate)

    setStep(step+1)
    if(refundType === 'Advance Funding') {
      if(window.afGTag && company.companyId === '6243c824-9e29-46da-94c6-96d03d8cbb84') {
        window.afGTag('event', 'conversion', {
          'send_to': 'AW-16699126403/iNzbCLLTj6IaEIPl4Zo-',
          'value': 1.0,
          'currency': 'USD'
        })
      }
      await handleAdvanceChosenStatus()
    }
  }

  const handleInitApplication = async (_validiFiBankVerification) => {
    const newValidiFiBankVerification = new ValidiFiBankVerification(_validiFiBankVerification)

    const form = new FormData()
    form.append('applicationInfo', JSON.stringify({
      leadId: customerData.customerLeadData.leadId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      refundAmount: customerData.customerLeadData.leadAmount,
      creditAmount: customerData.customerLeadData.leadAmount - lienAmount,
      interestAmount: interestAmount,
      loanInterestAmount: loanInterestAmount,
      lienAmount: lienAmount,
      newValidiFiBankVerification
    }))

    const createCustomerApplicationResponse = await fetch(`${apiURL}/api/createAdvanceApplication`, {
      method: 'POST',
      headers: {
        'Authorization': customerData.accessToken
      },
      body: form
    })
    const createCustomerApplicationData = await createCustomerApplicationResponse.json()
    if (createCustomerApplicationData.response.error) {
      if (createCustomerApplicationData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleInitApplication() : navigate('/', { state: { expired: true } })
      } else
        console.error(createCustomerApplicationData.response.error)
    } else {
      const advanceFundingApplication = new AdvanceFundingApplication(createCustomerApplicationData.response.advanceFundingApplication)
      customerData.customerAdvanceFunding = advanceFundingApplication
      setApplication(advanceFundingApplication)

      if(window.afGTag && company.companyId === '6243c824-9e29-46da-94c6-96d03d8cbb84') {
        window.afGTag('event', 'conversion', {
          'send_to': 'AW-16699126403/CCOICKjwkaIaEIPl4Zo-',
          'value': 1.0,
          'currency': 'USD'
        })
      }
    }
  }

  useEffect(() => {
    setShowLockBox(customerData.customerLeadData.leadAmount > 200 && !customerData.customerLockBoxInfo.lockBoxId)
    document.title = `${company.companyName} | Refund Calculated`
    posthog.startSessionRecording()

    return () => {
      posthog.stopSessionRecording()
    }
  }, [])

  return (
    <div className='w-screen flex flex-col items-center mt-4 font-mont'>
      {step === 0 ? (
        <RefundInfo
          step={step}
          setStep={setStep}
        />
      ) : step === 1 ? (
        !grabbingKYCData ? (
          showKYC ? (
            <IDMEVerification 
              kycError={kycError}
            />
          ) : (
            <RefundOptions
              applicationId={application.applicationId}
              setInterestAmount={setInterestAmount}
              setLienAmount={setLienAmount}
              handleSelectRefundType={handleSelectRefundType}
            />
          )
        ) : (
          <div className='flex flex-row items-center justify-center gap-2'>
            <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
            <p>Verifying KYC Data...</p>
          </div>
        )
      ) : step === 2 ? (
        <RefundTOS
          applicationId={application.applicationId}
          step={step}
          setStep={setStep}
        />
      ) : (
        refundType === 'Advance Funding' ? (
          step === 3 ? (
            <AdvancePaymentDetails
              interestAmount={interestAmount}
              lienAmount={lienAmount}
              step={step}
              setStep={setStep}
              _setLoanInterestAmount={setLoanInterestAmount}
            />
          ) : (
            <AdvancePaymentBankVerification
              step={step}
              setStep={setStep}
              setReload={setReload}
              handleInitApplication={handleInitApplication}
            />
          )
        ) : (
          step === 3 ? (
            shippingOption === '' ? (
              <RefundShippingOptions
                refundType={refundType}
                step={step}
                setStep={setStep}
                setShippingOption={setShippingOption}
              />
            ) : shippingOption === 'Shippo' ? (
              <RefundShippo
                applicationId={application.applicationId}
                step={step}
                refundType={refundType}
                setStep={setStep}
                setShippingOption={setShippingOption}
                handleGeneratedShippoLabel={null}
              />
            ) : (
              <></>
            )
          ) : (
            <RefundGenerateInvoice
              refundRate={refundRate}
              refundType={refundType}
            />
          )
        )
      )}
    </div>
  )
}